import type {
  HeroMedia,
  SiteBlueprint,
  SiteImage,
  SiteLink,
  SiteSection,
  SiteSectionItem,
} from '../types.js';

import type {
  PanderrBlueprint,
  PanderrBlueprintNavigationItem,
  PanderrBlueprintSection,
  PanderrBlueprintSectionItem,
} from '../templates/service-business-v1-adapter.js';

export interface LegacyPreviewBlueprint extends PanderrBlueprint {
  sourceUrl?: string;
  pageTitle?: string;
  pageHeading?: string;
  description?: string;
  heroHeading?: string;
  heroDescription?: string;
  heroImageUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  brandColor?: string;
  pearlColor?: string;
  colors?: string[];
  fonts?: string[];
  images?: Array<
    | string
    | {
        src?: string;
        url?: string;
        alt?: string;
        width?: number;
        height?: number;
        isLogo?: boolean;
      }
  >;
  heroMedia?: HeroMedia | null;
  capturedAt?: string;
}

export interface BlueprintNormalizationReport {
  heroCreated: boolean;
  heroImageRecovered: boolean;
  logoRecovered: boolean;
  colorsRecovered: number;
  cardsConverted: number;
  imagesRecovered: number;
  warnings: string[];
}

export interface NormalizedBlueprintResult {
  blueprint: SiteBlueprint;
  report: BlueprintNormalizationReport;
}

function text(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

function unique(values: unknown[]): string[] {
  return [...new Set(values.map(text).filter(Boolean))];
}

function normalizeImage(
  value:
    | string
    | {
        src?: string;
        url?: string;
        alt?: string;
        width?: number;
        height?: number;
        isLogo?: boolean;
      }
    | undefined,
  overrides: Partial<SiteImage> = {},
): SiteImage | null {
  const src = typeof value === 'string'
    ? text(value)
    : text(value?.src ?? value?.url);

  if (!src) return null;

  return {
    src,
    alt: overrides.alt ?? (typeof value === 'string' ? '' : text(value?.alt)),
    width: overrides.width ?? (typeof value === 'string' ? 0 : Number(value?.width) || 0),
    height: overrides.height ?? (typeof value === 'string' ? 0 : Number(value?.height) || 0),
    isLogo: overrides.isLogo ?? (typeof value === 'string' ? undefined : value?.isLogo),
  };
}

function dedupeImages(images: Array<SiteImage | null>): SiteImage[] {
  const seen = new Set<string>();
  return images.filter((image): image is SiteImage => {
    if (!image || seen.has(image.src)) return false;
    seen.add(image.src);
    return true;
  });
}

function normalizeLink(item: PanderrBlueprintNavigationItem): SiteLink | null {
  const label = text(item.label ?? item.text);
  const href = text(item.href ?? item.url);
  if (!label || !href) return null;

  const type: SiteLink['type'] = href.startsWith('tel:')
    ? 'phone'
    : href.startsWith('mailto:')
      ? 'email'
      : /^https?:\/\//i.test(href)
        ? 'external'
        : 'internal';

  return { text: label, href, type };
}

function normalizeItem(
  item: string | PanderrBlueprintSectionItem,
): string | SiteSectionItem {
  if (typeof item === 'string') return text(item);

  return {
    id: text(item.id) || undefined,
    title: text(item.title ?? item.heading) || undefined,
    text: text(item.text) || undefined,
    description: text(item.description) || undefined,
  };
}

function itemImage(item: string | PanderrBlueprintSectionItem): SiteImage | null {
  if (typeof item === 'string') return null;
  return normalizeImage(item.imageSrc ?? item.image);
}

function sectionType(section: PanderrBlueprintSection): SiteSection['type'] {
  const value = text(
    section.type ?? section.sourceType ?? section.semanticRole,
  ).toLowerCase();

  if (['hero', 'services', 'gallery', 'cta', 'contact'].includes(value)) {
    return value as SiteSection['type'];
  }
  if (/contact|quote|form/.test(value)) return 'contact';
  if (/gallery|portfolio|project/.test(value)) return 'gallery';
  if (/service|feature|benefit/.test(value)) return 'services';
  return 'content';
}

function normalizeSection(
  section: PanderrBlueprintSection,
  index: number,
): { section: SiteSection; images: SiteImage[]; cardCount: number } {
  const rawItems = [
    ...(section.items ?? []),
    ...(section.cards ?? []),
  ];

  const images = dedupeImages([
    ...(section.images ?? []).map((image) => normalizeImage(image)),
    ...rawItems.map(itemImage),
  ]);

  return {
    section: {
      id: text(section.id) || `section-${index + 1}`,
      type: sectionType(section),
      heading: text(section.heading) || undefined,
      paragraphs: unique([...(section.paragraphs ?? []), section.description]),
      items: rawItems.map(normalizeItem),
      images,
    },
    images,
    cardCount: section.cards?.length ?? 0,
  };
}

function createHeroSection(
  blueprint: LegacyPreviewBlueprint,
  heroImage: SiteImage | null,
): SiteSection | null {
  const heading = text(
    blueprint.heroHeading ??
      blueprint.pageHeading ??
      blueprint.pageTitle ??
      blueprint.title,
  );
  const description = text(
    blueprint.heroDescription ?? blueprint.description,
  );

  if (!heading && !description && !heroImage) return null;

  return {
    id: 'hero',
    type: 'hero',
    heading: heading || undefined,
    paragraphs: description ? [description] : [],
    items: [],
    images: heroImage ? [heroImage] : [],
  };
}

export function normalizeBlueprint(
  input: PanderrBlueprint,
): NormalizedBlueprintResult {
  const source = input as LegacyPreviewBlueprint;

  const logoImage = normalizeImage(
    source.logoSrc ?? source.logoUrl ?? source.logo,
    {
      isLogo: true,
      alt: text(source.logoAlt ?? source.businessName),
    },
  );

  const heroImage = normalizeImage(source.heroImageUrl, {
    alt: text(source.heroHeading ?? source.businessName),
  });

  const normalizedSections = (
    source.sections ?? source.sourceSections ?? []
  ).map(normalizeSection);

  const sourceSections = normalizedSections.map((entry) => entry.section);
  const hasHero = sourceSections.some((section) => section.type === 'hero');
  const createdHero = hasHero ? null : createHeroSection(source, heroImage);
  const sections = [...(createdHero ? [createdHero] : []), ...sourceSections];

  const images = dedupeImages([
    logoImage,
    heroImage,
    ...(source.images ?? []).map((image) => normalizeImage(image)),
    ...normalizedSections.flatMap((entry) => entry.images),
  ]);

  const colors = unique([
    ...(source.colors ?? []),
    source.primaryColor,
    source.secondaryColor,
    source.accentColor,
    source.brandColor,
    source.pearlColor,
  ]);

  const navigation = (
    source.navigation ?? source.navItems ?? source.menu ?? []
  )
    .map(normalizeLink)
    .filter((item): item is SiteLink => Boolean(item));

  const heroMedia: HeroMedia | null = source.heroMedia ?? (heroImage
    ? {
        id: 'hero-media-1',
        type: 'image',
        visible: true,
        src: heroImage.src,
        alt: heroImage.alt,
        placement: 'background',
        presentation: 'clean',
        border: 'none',
        radius: 'none',
        shadow: 'none',
        size: 'large',
        fit: 'cover',
        focalPoint: 'center',
        backgroundPosition: 'center',
        overlayStrength: 45,
      }
    : null);

  const warnings: string[] = [];
  if (!heroImage) warnings.push('No legacy hero image was available to normalize.');
  if (colors.length === 0) warnings.push('No brand colors were available to normalize.');

  return {
    blueprint: {
      sourceUrl: text(source.sourceUrl),
      title: text(source.pageTitle ?? source.title ?? source.businessName),
      pageHeading: text(source.heroHeading ?? source.pageHeading) || undefined,
      description: text(source.heroDescription ?? source.description) || undefined,
      businessName: text(source.businessName ?? source.siteName ?? source.title),
      images,
      sections,
      heroMedia,
      logo: logoImage ?? undefined,
      colors,
      fonts: unique(source.fonts ?? []),
      navigation,
      phone: text(source.phone) || undefined,
      email: text(source.email) || undefined,
      capturedAt: text(source.capturedAt) || new Date().toISOString(),
    },
    report: {
      heroCreated: Boolean(createdHero),
      heroImageRecovered: Boolean(heroImage),
      logoRecovered: Boolean(logoImage),
      colorsRecovered: colors.length,
      cardsConverted: normalizedSections.reduce(
        (sum, entry) => sum + entry.cardCount,
        0,
      ),
      imagesRecovered: images.length,
      warnings,
    },
  };
}
